import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, ArrowLeftIcon, LockIcon, SparklesIcon, UsersIcon, HeartIcon } from 'lucide-react';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { GeometricAccent } from '../components/ui/GeometricAccent';
import { useScreenInit } from '../useScreenInit.js';
const interests = ['Children Programs', 'Parent Membership', 'Adult Programs', 'Scholarships', 'Become a Teacher', 'Partnership / Donations'];
export function Waitlist() {
  const screenInit = useScreenInit();
  const [submitted, setSubmitted] = useState(screenInit.submitted ?? false);
  const [selected, setSelected] = useState<string[]>([]);
  const toggleInterest = (value: string) => {
    setSelected(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    // Mailchimp expects URL-encoded params, not multipart FormData
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value.toString());
    });
    try {
      await fetch('https://iilh.us5.list-manage.com/subscribe/post?u=436a7ad067b75120ed0ea7da1&id=7e4d2cd880&f_id=0048f2e0f0', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params.toString()
      });
    } catch {
      // no-cors fetch is opaque — we can't read errors, but submission still goes through
    }
    setSubmitted(true);
  };
  return <section className="relative overflow-hidden min-h-[80vh] bg-sage-50/60" data-id="element-4942" data-mp="yeijh8kntgpn">
      <Container className="relative py-16 md:py-24" data-id="element-4943" data-mp="tfxjna9bkz1q">
        <Link to="/en" className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-forest-700 transition mb-8" data-id="element-4944" data-mp="1j31cf612y4hy">
          <ArrowLeftIcon size={14} strokeWidth={1.6} data-id="element-4945" data-mp="1n5ek3zze0chd" />
          Back to home
        </Link>

        <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-4946" data-mp="1s2y6jn68nvke">
          {/* Left: intro */}
          <div className="lg:col-span-5" data-id="element-4947" data-mp="1x3lvm3awq2bz">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sand-100 text-sand-500 text-[11px] font-semibold tracking-[0.14em] uppercase border border-sand-300/50" data-id="element-4948" data-mp="1x0g7aqa3r4hb">
              Apply
            </span>
            <h1 className="font-display text-4xl md:text-5xl text-ink-900 leading-[1.05] mt-5" data-id="element-4949" data-mp="2atpeckakvdwo">
              Join the IILH
              <br data-id="element-4950" data-mp="2frz8qsohaah8" />
              Waitlist
            </h1>
            <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4951" data-mp="2ksnaa8ac29pp">
              Be among the first to access our programs as new cohorts and
              resources open. We'll notify you once a spot matching your
              interests becomes available.
            </p>

            <ul className="mt-8 space-y-4" data-id="element-4952" data-mp="e21shla2yt4y">
              {[{
              icon: SparklesIcon,
              text: 'Priority access to new programs and cohorts'
            }, {
              icon: UsersIcon,
              text: 'Personalized guidance to find your right path'
            }, {
              icon: HeartIcon,
              text: 'No commitment — leave anytime'
            }].map(b => <li key={b.text} className="flex items-start gap-3" data-id="element-4953" data-mp="rxvstfwsga6g">
                  <span className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-sage-100 text-forest-700 flex-shrink-0" data-id="element-4954" data-mp="n2zxo6w996p4">
                    <b.icon size={16} strokeWidth={1.6} data-id="element-4955" data-mp="1fku47z61wrxi" />
                  </span>
                  <span className="text-ink-700 leading-relaxed pt-1.5" data-id="element-4956" data-mp="1fmyjfd9ehdd3">
                    {b.text}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7" data-id="element-4957" data-mp="10c04ylp6jkgg">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="rounded-3xl bg-white border border-sage-200 shadow-card p-7 md:p-10" data-id="element-4958" data-mp="10d1rpnfk5ve0">
              {!submitted ? <form onSubmit={handleSubmit} action="https://iilh.us5.list-manage.com/subscribe/post?u=436a7ad067b75120ed0ea7da1&id=7e4d2cd880&f_id=0048f2e0f0" method="POST" data-id="element-4959" data-mp="14du24k0626bv">
                  <h2 className="font-display text-2xl text-ink-900" data-id="element-4960" data-mp="1wzmx47f80go3">
                    Your details
                  </h2>
                  <p className="mt-1 text-sm text-ink-500" data-id="element-4961" data-mp="3milhfeins3xq">
                    All fields are confidential.
                  </p>

                  <div className="mt-7 grid sm:grid-cols-2 gap-4" data-id="element-4962" data-mp="3hiqrpeamdke9">
                    <Field label="Full name" name="FNAME" placeholder="Your full name" required data-id="element-4963" data-mp="30sdegky4a2" />
                    <Field label="Email" name="EMAIL" type="email" placeholder="you@example.com" required data-id="element-4964" data-mp="33jr5edakxhl9" />
                    <Field label="Country" name="COUNTRY" placeholder="Where you are based" data-id="element-4965" data-mp="3clgvnfsq6ahg" />
                    <div data-id="element-4966" data-mp="rtoogc3ecjgv">
                      <label className="block text-sm font-medium text-ink-900 mb-1.5" data-id="element-4967" data-mp="rsvgx8wm5pxo">
                        I am a…
                      </label>
                      <select name="ROLE" className="w-full rounded-xl border border-sage-200 bg-ivory/40 px-4 py-3 text-[15px] text-ink-900 focus:outline-none focus:border-forest-700 focus:bg-white transition appearance-none" data-id="element-4968" data-mp="3gnlga9bbjqqd">
                        <option data-id="element-4969" data-mp="3umuqmscufqmf">Parent</option>
                        <option data-id="element-4970" data-mp="3umkxivwi53ci">Adult learner</option>
                        <option data-id="element-4971" data-mp="2xxdk6b61e0jo">Educator / Mentor</option>
                        <option data-id="element-4972" data-mp="2xur23hlis2n7">Donor / Partner</option>
                        <option data-id="element-4973" data-mp="32v58kv7h34j4">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-6" data-id="element-4974" data-mp="36vz161vv17ic">
                    <label className="block text-sm font-medium text-ink-900 mb-2.5" data-id="element-4975" data-mp="3bo8ln2e7hfd8">
                      What are you interested in?{' '}
                      <span className="text-ink-500 font-normal" data-id="element-4976" data-mp="2f5krkhuvqhch">
                        (select any)
                      </span>
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-4977" data-mp="2bo6hjnkqulyk">
                      {interests.map(opt => {
                    const active = selected.includes(opt);
                    return <button key={opt} type="button" onClick={() => toggleInterest(opt)} className={`px-4 py-2 rounded-full text-sm font-medium border transition ${active ? 'bg-forest-700 text-ivory border-forest-700' : 'bg-white text-ink-700 border-sage-200 hover:border-forest-700/40'}`} data-id="element-4978" data-mp="22ni5uzitoxou">
                            {opt}
                          </button>;
                  })}
                    </div>
                    {/* Submit interests to Mailchimp as a comma-separated list */}
                    <input type="hidden" name="INTEREST" value={selected.join(', ')} data-id="element-4979" data-mp="1a15ptmitniud" />
                  </div>

                  <div className="mt-6" data-id="element-4980" data-mp="2sxh9ml64duot">
                    <label className="block text-sm font-medium text-ink-900 mb-1.5" data-id="element-4981" data-mp="2xwal681ujbmc">
                      Anything we should know?{' '}
                      <span className="text-ink-500 font-normal" data-id="element-4982" data-mp="2jvhophrzi6gl">
                        (optional)
                      </span>
                    </label>
                    <textarea name="NOTES" rows={4} placeholder="Tell us about your family or learning goals…" className="w-full rounded-xl border border-sage-200 bg-ivory/40 px-4 py-3 text-[15px] focus:outline-none focus:border-forest-700 focus:bg-white transition" data-id="element-4983" data-mp="2a6g6dkxyvczg" />
                  </div>

                  <label className="mt-6 flex items-start gap-3 cursor-pointer" data-id="element-4984" data-mp="1r6sk38rpzj03">
                    <input type="checkbox" required className="mt-1 w-4 h-4 rounded border-sage-200 text-forest-700 focus:ring-forest-700" data-id="element-4985" data-mp="1w6f7uc56chn5" />
                    <span className="text-sm text-ink-700 leading-relaxed" data-id="element-4986" data-mp="17mbw6mw181u9">
                      I agree to be contacted about programs that match my
                      interests. My data is handled confidentially.
                    </span>
                  </label>

                  {/* Mailchimp bot-protection honeypot — must remain empty */}
                  <div style={{
                position: 'absolute',
                left: '-5000px'
              }} aria-hidden="true" data-id="element-4987" data-mp="1vheioo7d5t7k">
                    <input type="text" name="b_436a7ad067b75120ed0ea7da1_7e4d2cd880" tabIndex={-1} defaultValue="" data-id="element-4988" data-mp="20a7ntd6qv3k6" />
                  </div>

                  <div className="mt-7 flex items-center justify-between gap-4 flex-wrap" data-id="element-4989" data-mp="2a9orxq6cl9e1">
                    <p className="text-xs text-ink-500 flex items-center gap-2" data-id="element-4990" data-mp="25sca2fkkzfzs">
                      <LockIcon size={12} strokeWidth={1.8} data-id="element-4991" data-mp="210asce4n8x2d" />
                      Your data is encrypted and never shared.
                    </p>
                    <Button variant="primary" size="md" type="submit" data-id="element-4992" data-mp="1w1r8cj9m1vcn">
                      Join
                    </Button>
                  </div>
                </form> : <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4
            }} className="text-center py-8" data-id="element-4993" data-mp="2opdiybh9a090">
                  <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-sage-100 text-forest-700 mb-5" data-id="element-4994" data-mp="13e52t5gtogn5">
                    <CheckCircle2Icon size={28} strokeWidth={1.6} data-id="element-4995" data-mp="3aykd0aohg6ii" />
                  </span>
                  <h2 className="font-display text-3xl text-ink-900" data-id="element-4996" data-mp="3awr4sqy81e8e">
                    You're on the waiting list
                  </h2>
                  <p className="mt-3 text-ink-700 max-w-md mx-auto leading-relaxed" data-id="element-4997" data-mp="3jy7dmu9e18ai">
                    Thank you for joining the IILH waitlist. We'll be in touch
                    as soon as a program matching your interests becomes
                    available.
                  </p>
                  <div className="mt-7 flex justify-center gap-3 flex-wrap" data-id="element-4998" data-mp="2hz9vaij87nb">
                    <Link to="/en" data-id="element-4999" data-mp="6kltio51um9f">
                      <Button variant="secondary" data-id="element-5000" data-mp="6lf11rbu1fsm">Return Home</Button>
                    </Link>
                    <Link to="/en/about" data-id="element-5001" data-mp="bidxwhmvs0ui">
                      <Button variant="primary" data-id="element-5002" data-mp="bhchdxn3im5e">Learn About Us</Button>
                    </Link>
                  </div>
                </motion.div>}
            </motion.div>
          </div>
        </div>
      </Container>
    </section>;
}
function Field({
  label,
  placeholder,
  type = 'text',
  required = false,
  name
}: {
  label: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
  name?: string;
}) {
  return <div data-id="element-5003" data-mp="3pngsw9mnqtv9">
      <label className="block text-sm font-medium text-ink-900 mb-1.5" data-id="element-5004" data-mp="3pn724jtsgioi">
        {label}
        {required && <span className="text-sand-500 ml-1" data-id="element-5005" data-mp="3kmjdg4t18dxg">*</span>}
      </label>
      <input type={type} name={name} required={required} placeholder={placeholder} className="w-full rounded-xl border border-sage-200 bg-ivory/40 px-4 py-3 text-[15px] focus:outline-none focus:border-forest-700 focus:bg-white transition" data-id="element-5006" data-mp="3enwhromtm64" />
    </div>;
}